import { ft } from '../core/units.js';
import { segmentClear } from '../core/collision.js';
import { turnToward } from '../core/character.js';
import { parseTime } from './clock.js';
import { createRng, hashSeed } from './rng.js';

/**
 * Somebody walking a route.
 *
 * No pathfinding, no steering, no collision. The route is authored to be walkable
 * (there is a test that proves it), so an NPC that simply moves along it will
 * never be inside a wall — and the moment you add avoidance, the patrol stops
 * being learnable, which is the only thing making it worth avoiding.
 *
 * The vision cone is three tests in the order they get cheaper to fail: range,
 * then angle, then line of sight. The line of sight is a real segment test
 * against the building, because a manager who sees you through the storage room
 * wall turns the write-up system from a stealth problem into an unfair one.
 *
 * Most of them keep hours. Outside their window they are not standing somewhere
 * else, they are simply gone — not drawn, not talkable, and with their eyes
 * shut. Modelling where the Night Steward is at two in the afternoon would be a
 * second patrol system for a person nobody can interact with; "has gone home"
 * is the truthful answer and the cheap one, and it is what makes the windows
 * legible: the corridor visibly empties out.
 */

/**
 * Whether a window covers a time of day.
 *
 * Handles wrapping, because a night shift's window starts after it ends.
 */
export function windowCovers(window, minutes) {
  if (!window) return true;
  const from = parseTime(window.from);
  const to = parseTime(window.to);
  if (from <= to) return minutes >= from && minutes < to;
  return minutes >= from || minutes < to;
}

/** Monday first, because day one is a Monday and the schedule reads that way. */
export function weekdayOf(day) {
  return ((day - 1) % 7 + 7) % 7;
}

/**
 * Whether somebody is on the floor right now — the window, and the two things
 * a roster row is allowed to say on top of it.
 *
 * `windowCovers` above is the whole of availability for anybody who keeps the
 * same hours every day, which is most of the building, and it stays exactly
 * where it was because the negotiation path reads it and a gate's hours are not
 * allowed to be a surprise. These two are for the crew, who are colleagues
 * rather than doors, and for whom being hard to catch is a characteristic
 * rather than a fault:
 *
 *   `days`    which days of the week they work at all. Somebody who is in on
 *             Friday, Saturday and Sunday is not a shorter window, they are a
 *             different problem — you can plan around a person who leaves at
 *             half two and you cannot plan around a person who is not in.
 *   `drifts`  turning up late, on a seeded fraction of days. Rolled off the day
 *             rather than off the moment, so it is a person you cannot count on
 *             and not a coin you can flip until it lands: ask twice on a
 *             Tuesday and Tuesday gives the same answer both times.
 *
 * Pure, and seeded from the id and the day, so a reload puts the same people on
 * the same floor.
 */
export function onDutyAt(data, clock, seed = 'lion') {
  if (!windowCovers(data.window, clock.minutes)) return false;
  if (data.days && !data.days.includes(weekdayOf(clock.day))) return false;
  if (!data.drifts) return true;

  const roll = createRng(hashSeed(seed, 'drift', data.id, clock.day));
  if (roll.next() >= data.drifts.odds) return true;
  /*
   * Late, not absent. The end of the window is where it was — he does not stay
   * on to make it up, which is the other half of the same character.
   *
   * Only meaningful for a window that does not wrap past midnight, and no row
   * has both. A wrapping window with a late start would need to know which side
   * of midnight it is on, and the honest thing is to say so rather than to
   * produce an answer nobody can check.
   */
  const from = parseTime(data.window.from);
  const to = parseTime(data.window.to);
  if (to <= from) return true;
  return clock.minutes >= from + data.drifts.lateBy;
}

/**
 * `sight` is a multiplier on how far this person can see, asked every frame.
 *
 * One number, defaulted to all of it, and the only reason it exists is that
 * sight is a thing the world can take away: a nose full of pepper is a smaller
 * cone for a minute. It is asked rather than set so that whoever is doing it to
 * them owns the timer, and this file never learns what pepper is.
 */
/*
 * `onProperty` is the story's say on top of their hours, asked every frame
 * with the clock: the man from the ownership group is only here on the days
 * he tours, and somebody walked out is not here at all. True for everybody
 * the story has no opinion about, which is everybody else.
 */
export function createNpc({ data, world, sight = () => 1, onProperty = () => true }) {
  const route = data.route.map(([x, z, dwell = 0]) => ({ x: ft(x), z: ft(z), dwell }));
  const position = { x: route[0].x, y: 0, z: route[0].z };
  let index = 0;
  let waiting = 0;
  /**
   * Facing along the route from the start, not at +Z.
   *
   * Most of the thirteen stand at a post and shuffle between two points, and
   * they spend most of a lap stationary at the first one. Starting them facing
   * an arbitrary compass direction meant a gatekeeper could be looking at a
   * wall for six seconds out of every ten while somebody emptied her cart.
   */
  /*
   * Or the way their row says, for somebody who sits rather than walks: a
   * guest at a lawn dinner faces down the lawn, and a one-point route has no
   * direction of its own to give them.
   */
  const facing =
    data.faces ?? (route.length > 1 ? Math.atan2(route[1].x - route[0].x, route[1].z - route[0].z) : 0);
  let yaw = facing;
  /** 0..1. How close this NPC is to writing you up. */
  let suspicion = 0;

  /**
   * Somewhere they are going that is not on their route.
   *
   * One detour at a time and no pathfinding, same as the route. If the last
   * place they saw you is through a wall they will walk at the wall and give up
   * on it, which is both cheap and roughly what a person does.
   */
  let detour = null;
  let detourFor = 0;

  /**
   * Standing still, and what they are turning to look at while they do it.
   *
   * The other half of `goAndLook`, and the two of them are the whole vocabulary
   * a person has for showing you what they are about to do. One is going
   * somewhere; this is deciding not to. A coverer looking at the floor and
   * somebody who has stopped to watch you are the same mechanic pointed two
   * different ways, which is why `away` is a flag rather than a second method.
   */
  let holding = 0;
  let holdAt = null;
  let holdAway = false;

  /**
   * A few words over their head, and when they stop being true.
   *
   * The only thing in this module that exists to be *read* rather than to be
   * true. A greybox person has no face and no animation, so a tell they perform
   * physically — walking off toward the office — is legible as movement and
   * completely ambiguous as intent: they might be going to the office for any
   * reason at all. The words are what make the movement mean something the
   * first time you see it, and after that the movement is enough.
   *
   * Timed off the same summed frames everything else here runs on, so a paused
   * game does not lose them.
   */
  let note = null;
  let noteFor = 0;

  /**
   * Held off the floor regardless of the clock.
   *
   * A measurement instrument, and the only one in the simulation. "How long
   * does a corridor run take with nobody in the corridor" is a real question
   * with a real answer, and there is no time of day at which the Banquet
   * Manager is not walking up and down it. Nothing in the game sets this.
   */
  let heldBack = false;

  /**
   * Sitting in something that is going somewhere, and the way back from it.
   *
   * A passenger is still a person: on the floor, eyes open, facing whichever
   * way their seat faces, and able to be talked to, suspicious, or struck by a
   * branch. What they are not doing is walking their route, so `aboard` is
   * where the seat says they are, every frame.
   *
   * Getting off anywhere but their post leaves them off their route, and a
   * person here has no pathfinding and no collision. So they walk back the way
   * they came — `returning` is the trail the ride left, which a cart wider
   * than a person has already proved a body fits along — and pick the route
   * up where they left it.
   */
  let aboard = null;
  let returning = [];

  /**
   * Sent somewhere to do a job, and the route they walk while they are there.
   *
   * Their own route is still their own; this is a second one laid over it for
   * as long as the job lasts, walked the same way — no pathfinding, a dwell at
   * each point — so a colleague out striking a lawn is a person walking a
   * route like every other hour of their day, with eyes, hours and a shape.
   * Whoever posted them decides where the points are, and moves them.
   */
  let posted = null;
  let postIndex = 0;

  /**
   * Getting up and going: a path to walk, and then gone until their hours come
   * round again. Not a shift ending — a guest who has had enough of a dinner
   * leaves it, and is not at that table again tonight whatever the clock says.
   * Whoever decides they have had enough decides the path.
   */
  let leaving = [];
  let gone = false;
  /** The day they left on. Gone is for that evening and no other. */
  let goneDay = null;

  let present = true;
  const eyeHeight = 1.62;
  const fullRange = data.vision ? ft(data.vision.range) : 0;
  const range = () => fullRange * Math.max(0, Math.min(1, sight()));
  const cosHalf = data.vision ? Math.cos((data.vision.halfAngle * Math.PI) / 180) : -1;

  function update(dt, clock = null) {
    if (clock) {
      // Somebody in the back of a cart is there until they are let out, hours
      // or not: a guest does not vanish off the seat at ten o'clock halfway up
      // the cart road.
      const on = (onDutyAt(data, clock) && onProperty(data, clock)) || Boolean(aboard);
      // Gone is for tonight. Once their hours are over it is a new evening.
      if (gone && goneDay === null) goneDay = clock.day;
      if (!on || (gone && clock.day !== goneDay)) {
        gone = false;
        goneDay = null;
      }
      const now = !heldBack && !gone && on;
      if (now !== present) {
        present = now;
        // Back at the top of the route when they come on, so a post is where
        // you expect it to be rather than wherever they were when they left.
        if (present) {
          index = 0;
          postIndex = 0;
          waiting = 0;
          const start = (posted ?? route)[0];
          position.x = start.x;
          position.z = start.z;
          yaw = data.faces ?? yaw;
        }
        suspicion = 0;
      }
    }
    if (heldBack) {
      present = false;
      return;
    }
    if (!present) {
      aboard = null;
      returning = [];
      return;
    }

    if (aboard) {
      const seat = aboard();
      if (seat) {
        position.x = seat.x;
        position.y = seat.y ?? 0;
        position.z = seat.z;
        yaw = seat.yaw ?? yaw;
      }
      if (noteFor > 0) {
        noteFor -= dt;
        if (noteFor <= 0) note = null;
      }
      return;
    }
    // On the floor, or stood in water up to their waist.
    position.y = data.wading ? -ft(data.wading) : 0;

    if (leaving.length) {
      const next = leaving[0];
      const gap = Math.hypot(next.x - position.x, next.z - position.z);
      if (gap < 0.3) {
        leaving.shift();
        if (!leaving.length) {
          gone = true;
          present = false;
          suspicion = 0;
        }
      } else {
        const step = Math.min(data.speed * dt, gap);
        position.x += ((next.x - position.x) / gap) * step;
        position.z += ((next.z - position.z) / gap) * step;
        yaw = turnToward(yaw, Math.atan2(next.x - position.x, next.z - position.z), 5 * dt);
      }
      if (noteFor > 0) {
        noteFor -= dt;
        if (noteFor <= 0) note = null;
      }
      return;
    }

    if (returning.length && !detour && holding <= 0) {
      const next = returning[0];
      const gap = Math.hypot(next.x - position.x, next.z - position.z);
      if (gap < 0.3) {
        returning.shift();
      } else {
        const step = Math.min(data.speed * dt, gap);
        position.x += ((next.x - position.x) / gap) * step;
        position.z += ((next.z - position.z) / gap) * step;
        yaw = turnToward(yaw, Math.atan2(next.x - position.x, next.z - position.z), 5 * dt);
        if (noteFor > 0) {
          noteFor -= dt;
          if (noteFor <= 0) note = null;
        }
        return;
      }
    }

    if (noteFor > 0) {
      noteFor -= dt;
      if (noteFor <= 0) note = null;
    }

    if (holding > 0) {
      holding -= dt;
      if (holdAt) {
        const dx = holdAt.x - position.x;
        const dz = holdAt.z - position.z;
        if (Math.hypot(dx, dz) > 1e-3) {
          const toward = Math.atan2(dx, dz) + (holdAway ? Math.PI : 0);
          yaw = turnToward(yaw, toward, 5 * dt);
        }
      }
      return;
    }

    if (detour) {
      detourFor += dt;
      const gap = Math.hypot(detour.x - position.x, detour.z - position.z);
      if (gap < 0.5 || detourFor > 14) {
        detour = null;
        detourFor = 0;
      } else {
        const step = Math.min(data.speed * dt, gap);
        position.x += ((detour.x - position.x) / gap) * step;
        position.z += ((detour.z - position.z) / gap) * step;
        yaw = turnToward(yaw, Math.atan2(detour.x - position.x, detour.z - position.z), 5 * dt);
        return;
      }
    }

    const path = posted ?? route;
    const at = posted ? postIndex : index;
    const target = path[at % path.length];
    const dx = target.x - position.x;
    const dz = target.z - position.z;
    const distance = Math.hypot(dx, dz);

    if (distance < 0.12) {
      if (waiting < target.dwell) {
        waiting += dt;
        return;
      }
      waiting = 0;
      if (posted) postIndex = (at + 1) % path.length;
      else index = (at + 1) % path.length;
      return;
    }

    const step = Math.min(data.speed * dt, distance);
    position.x += (dx / distance) * step;
    position.z += (dz / distance) * step;
    // Turning rather than snapping, so a corner reads as a person looking
    // around it instead of a cone teleporting sideways.
    yaw = turnToward(yaw, Math.atan2(dx, dz), 5 * dt);
  }

  /** Whether a point is inside the cone, with a clear line to it. */
  function sees(point, { eye = 1.2 } = {}) {
    if (!data.vision || !present) return false;
    const dx = point.x - position.x;
    const dz = point.z - position.z;
    const distance = Math.hypot(dx, dz);
    const reach = range();
    if (distance > reach || distance < 1e-3) return distance <= reach;

    const facing = (dx / distance) * Math.sin(yaw) + (dz / distance) * Math.cos(yaw);
    if (facing < cosHalf) return false;

    return segmentClear(
      { x: position.x, y: eyeHeight, z: position.z },
      { x: point.x, y: eye, z: point.z },
      world
    );
  }

  return {
    data,
    route,
    get position() {
      return position;
    },
    get yaw() {
      return yaw;
    },
    get suspicion() {
      return suspicion;
    },
    /** Off the floor, for a measurement. Restored by passing false. */
    holdBack(value) {
      heldBack = Boolean(value);
      if (heldBack) present = false;
      return heldBack;
    },
    get heldBack() {
      return heldBack;
    },

    get hasVision() {
      return Boolean(data.vision);
    },
    /**
     * How far they can actually see this frame, in metres.
     *
     * The row's range with whatever the world is doing to them applied. Read by
     * perception, so a person who cannot see far is also less sure about what
     * they did see — which is what "reduced vision" has to mean if it is going
     * to mean anything.
     */
    get sightRange() {
      return range();
    },
    /** On the floor right now. Off-shift staff are not anywhere. */
    get present() {
      return present;
    },
    get department() {
      return data.department;
    },
    update,
    sees,

    /**
     * Sits down in something. `seat` is asked every frame for where they are
     * and which way they face; nothing here knows it is a cart.
     */
    board(seat) {
      if (!present || aboard) return false;
      aboard = seat;
      detour = null;
      holding = 0;
      returning = [];
      return true;
    },

    /**
     * Walks `trail`, points in metres, and then picks their route up again:
     * back from a break the way they came. Nothing else changes about them.
     */
    walkBack(trail = []) {
      returning = trail.map((point) => ({ x: point.x, z: point.z }));
      return true;
    },

    /** Gets out, and walks `trail` — a list of points — back to their route. */
    alight(trail = []) {
      if (!aboard) return false;
      aboard = null;
      position.y = 0;
      returning = trail.map((point) => ({ x: point.x, z: point.z }));
      return true;
    },

    get aboard() {
      return Boolean(aboard);
    },
    get returning() {
      return returning.length > 0;
    },

    /**
     * Go and look at somewhere. Used when somebody is suspicious and has just
     * lost sight of you; nothing in here knows that is why.
     */
    goAndLook(at) {
      if (!present || aboard) return false;
      detour = { x: at.x, z: at.z };
      detourFor = 0;
      holding = 0;
      return true;
    },
    get searching() {
      return Boolean(detour);
    },

    /**
     * Stop where you are for a moment, looking at something — or pointedly not.
     *
     * Cancels a detour, because a person cannot be walking somewhere and
     * standing still, and whichever was asked for last is what they are doing.
     */
    hold(seconds, { at = null, away = false } = {}) {
      if (!present || aboard) return false;
      holding = seconds;
      holdAt = at ? { x: at.x, z: at.z } : null;
      holdAway = away;
      detour = null;
      detourFor = 0;
      return true;
    },
    get holding() {
      return holding > 0;
    },

    /** A few words over their head, for `seconds`. Nothing reads them but the view. */
    say(text, seconds = 4) {
      if (!present) return false;
      note = text;
      noteFor = seconds;
      return true;
    },
    get note() {
      return noteFor > 0 ? note : null;
    },

    /**
     * Builds or bleeds suspicion. Returns true on the frame it fills.
     *
     * It bleeds at half the rate it builds, so ducking into the storage room
     * for a second buys you something without wiping the slate — and being seen
     * twice in quick succession is worse than being seen once.
     */
    watch(dt, caught) {
      if (!data.vision || !present) return false;
      if (caught) {
        suspicion += dt / data.vision.dwell;
        if (suspicion >= 1) {
          suspicion = 0;
          return true;
        }
      } else {
        suspicion = Math.max(0, suspicion - dt / (data.vision.dwell * 2));
      }
      return false;
    },

    clearSuspicion() {
      suspicion = 0;
    },

    /**
     * Sent to do a job: `path` is points in metres with a dwell in seconds, and
     * `from` puts them at the start of it, for somebody who was already out
     * there when you arrived. Posting again replaces the path and keeps where
     * they are, which is how whoever posted them moves the job about.
     */
    post(path, { from = false, now = false } = {}) {
      if (!path?.length) return false;
      const changed = !posted;
      posted = path.map((point) => ({ x: point.x, z: point.z, dwell: point.dwell ?? 0 }));
      if (changed || from) {
        postIndex = 0;
        waiting = 0;
      }
      postIndex %= posted.length;
      if (from) {
        position.x = posted[0].x;
        position.z = posted[0].z;
      }
      // `now`: straight to it from where they stand, whatever they were doing,
      // walking back up a cart road included.
      if (from || now) {
        detour = null;
        holding = 0;
        returning = [];
      }
      return true;
    },

    /**
     * Off the job and back at their own post, the way somebody comes on shift.
     * `home: false` leaves them where they are to walk their route from there.
     */
    unpost({ home = true } = {}) {
      if (!posted) return false;
      posted = null;
      postIndex = 0;
      index = 0;
      waiting = 0;
      detour = null;
      holding = 0;
      returning = [];
      if (!aboard && home) {
        position.x = route[0].x;
        position.z = route[0].z;
      }
      return true;
    },

    get posted() {
      return posted ? posted.map((point) => ({ ...point })) : null;
    },

    /** Which point of the job they are walking to, and whether they are stood at it. */
    get postedAt() {
      if (!posted) return null;
      const target = posted[postIndex];
      return {
        index: postIndex,
        arrived: Math.hypot(target.x - position.x, target.z - position.z) < 0.12,
      };
    },

    /** Gets up and walks `path` — points in metres — and is gone at the end of it. */
    leave(path = [], { day = null } = {}) {
      if (!present || aboard || leaving.length) return false;
      leaving = path.map((point) => ({ x: point.x, z: point.z }));
      goneDay = day;
      detour = null;
      holding = 0;
      returning = [];
      if (!leaving.length) {
        gone = true;
        present = false;
      }
      return true;
    },
    /** On the way out. */
    get leaving() {
      return leaving.length > 0;
    },
    /** Left, and not back tonight. */
    get gone() {
      return gone;
    },

    /** Puts them back at the top of the route. Used when a run resets. */
    reset() {
      leaving = [];
      gone = false;
      goneDay = null;
      yaw = facing;
      posted = null;
      postIndex = 0;
      index = 0;
      waiting = 0;
      detour = null;
      detourFor = 0;
      holding = 0;
      holdAt = null;
      note = null;
      noteFor = 0;
      suspicion = 0;
      heldBack = false;
      present = true;
      aboard = null;
      returning = [];
      position.y = 0;
      position.x = route[0].x;
      position.z = route[0].z;
    },
  };
}
